import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { format, parseISO, formatDistanceToNow } from "date-fns";
import { MessageSquare, Send, ArrowLeft, User, CheckCheck, Check } from "lucide-react";
import { toast } from "sonner";

interface ClinicianMessagesProps {
  clinicianId: string;
  clients: any[];
}

interface Message {
  id: string;
  sender_id: string;
  recipient_id: string;
  clinician_client_id: string;
  content: string;
  read_at: string | null;
  created_at: string;
}

interface ConversationPreview {
  clinician_client_id: string;
  client_id: string;
  client_name: string;
  last_message: string;
  last_message_time: string;
  unread_count: number;
}

export function ClinicianMessages({ clinicianId, clients }: ClinicianMessagesProps) {
  const [conversations, setConversations] = useState<ConversationPreview[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<string | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    loadConversations();

    // Subscribe to new messages
    const channel = supabase
      .channel('clinician-messages')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'clinician_messages',
          filter: `recipient_id=eq.${clinicianId}`
        },
        () => {
          loadConversations();
          if (selectedConversation) {
            loadMessages(selectedConversation);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [clinicianId]);

  const loadConversations = async () => {
    const { data: messagesData, error } = await supabase
      .from('clinician_messages')
      .select('*')
      .or(`sender_id.eq.${clinicianId},recipient_id.eq.${clinicianId}`)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error loading conversations:', error);
      setLoading(false);
      return;
    }

    // Group by clinician_client_id
    const conversationMap = new Map<string, ConversationPreview>();
    
    for (const msg of messagesData || []) {
      if (!msg.clinician_client_id) continue;
      
      if (!conversationMap.has(msg.clinician_client_id)) {
        const client = clients.find(c => c.id === msg.clinician_client_id);
        const clientProfile = client?.profiles;
        
        conversationMap.set(msg.clinician_client_id, {
          clinician_client_id: msg.clinician_client_id,
          client_id: msg.sender_id === clinicianId ? msg.recipient_id : msg.sender_id,
          client_name: clientProfile?.full_name || clientProfile?.email || 'Unknown Client',
          last_message: msg.content,
          last_message_time: msg.created_at,
          unread_count: 0
        });
      }
      
      if (msg.recipient_id === clinicianId && !msg.read_at) {
        const conv = conversationMap.get(msg.clinician_client_id)!;
        conv.unread_count++;
      }
    }

    setConversations(Array.from(conversationMap.values()));
    setLoading(false);
  };

  const loadMessages = async (clinicianClientId: string) => {
    const { data, error } = await supabase
      .from('clinician_messages')
      .select('*')
      .eq('clinician_client_id', clinicianClientId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('Error loading messages:', error);
      return;
    }

    setMessages(data || []);

    // Mark as read
    const unreadIds = (data || [])
      .filter(m => m.recipient_id === clinicianId && !m.read_at)
      .map(m => m.id);

    if (unreadIds.length > 0) {
      await supabase
        .from('clinician_messages')
        .update({ read_at: new Date().toISOString() })
        .in('id', unreadIds);
      
      loadConversations();
    }
  };

  const handleSelectConversation = (clinicianClientId: string) => {
    setSelectedConversation(clinicianClientId);
    loadMessages(clinicianClientId);
  };

  const handleSend = async () => {
    if (!newMessage.trim() || !selectedConversation) return;

    const conv = conversations.find(c => c.clinician_client_id === selectedConversation);
    if (!conv) return;

    setSending(true);
    const { error } = await supabase
      .from('clinician_messages')
      .insert({
        sender_id: clinicianId,
        recipient_id: conv.client_id,
        clinician_client_id: selectedConversation,
        message_type: 'general',
        content: newMessage.trim()
      });

    if (error) {
      console.error('Error sending message:', error);
      toast.error('Failed to send message');
      setSending(false);
      return;
    }

    const newMsg: Message = {
      id: crypto.randomUUID(),
      sender_id: clinicianId,
      recipient_id: conv.client_id,
      clinician_client_id: selectedConversation,
      content: newMessage.trim(),
      read_at: null,
      created_at: new Date().toISOString()
    };
    setMessages(prev => [...prev, newMsg]);
    setNewMessage("");
    setSending(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const selectedConv = conversations.find(c => c.clinician_client_id === selectedConversation);

  return (
    <Card className="h-[600px] flex flex-col bg-card/50 border-border/30">
      <CardHeader className="border-b border-border/30">
        <div className="flex items-centre gap-2">
          {selectedConversation && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setSelectedConversation(null)}
              className="text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div>
            <CardTitle className="flex items-centre gap-2 text-foreground">
              <MessageSquare className="h-5 w-5 text-primary" />
              {selectedConv ? selectedConv.client_name : 'Messages'}
            </CardTitle>
            {!selectedConversation && (
              <CardDescription className="text-muted-foreground">
                Client messages for between-session support
              </CardDescription>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent className="flex-1 flex flex-col p-0 overflow-hidden">
        {!selectedConversation ? (
          <ScrollArea className="flex-1">
            {conversations.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No messages yet</p>
                <p className="text-sm">Messages from clients will appear here</p>
              </div>
            ) : (
              <div className="divide-y">
                {conversations.map((conv) => (
                  <button
                    key={conv.clinician_client_id}
                    onClick={() => handleSelectConversation(conv.clinician_client_id)}
                    className="w-full p-4 text-left hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{conv.client_name}</span>
                          <span className="text-xs text-muted-foreground whitespace-nowrap">
                            {formatDistanceToNow(parseISO(conv.last_message_time), { addSuffix: true })}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{conv.last_message}</p>
                      </div>
                      {conv.unread_count > 0 && (
                        <Badge className="bg-primary">{conv.unread_count}</Badge>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </ScrollArea>
        ) : (
          <>
            <ScrollArea className="flex-1 p-4">
              <div className="space-y-4">
                {messages.map((message) => {
                  const isFromMe = message.sender_id === clinicianId;
                  return (
                    <div
                      key={message.id}
                      className={`flex ${isFromMe ? 'justify-end' : 'justify-start'}`}
                    >
                      <div
                        className={`max-w-[80%] rounded-lg px-4 py-2 ${
                          isFromMe
                            ? 'bg-primary text-primary-foreground'
                            : 'bg-muted'
                        }`}
                      >
                        <p className="whitespace-pre-wrap break-words">{message.content}</p>
                        <div className={`flex items-center gap-1 text-xs mt-1 ${
                          isFromMe ? 'text-primary-foreground/70' : 'text-muted-foreground'
                        }`}>
                          <span>{format(parseISO(message.created_at), 'HH:mm')}</span>
                          {isFromMe && (
                            message.read_at ? (
                              <CheckCheck className="h-3 w-3" />
                            ) : (
                              <Check className="h-3 w-3" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
            
            <div className="p-4 border-t bg-background">
              <div className="flex gap-2">
                <Textarea
                  placeholder="Type a message..."
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend();
                    }
                  }}
                  className="min-h-[60px] resize-none"
                  disabled={sending}
                />
                <Button 
                  onClick={handleSend} 
                  disabled={!newMessage.trim() || sending}
                  className="self-end"
                >
                  <Send className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
